import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
    inject,
} from '@angular/core';
import { BehaviorSubject, Subscription, catchError, debounceTime, filter, of, switchMap, tap } from 'rxjs';

import { Container, setGrow } from '../../animations/grow.animation';
import { HttpService } from '../../services/http.service';
import { UserService } from '@app/user';

@Component({
    selector: 'app-terms-results',
    templateUrl: './terms-results.component.html',
    styleUrls: ['./terms-results.component.scss'],
    animations: [Container, setGrow('0.4s', '0.4s')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TermsResultsComponent implements OnInit, OnDestroy, OnChanges {
    @Input() value: string;
    @Input() focus: boolean;
    @Output() selected = new EventEmitter<string>();
    @ViewChildren('dropdownItem') items: QueryList<ElementRef>;

    private readonly httpService = inject(HttpService);
    private readonly userService = inject(UserService);
    private readonly cdr = inject(ChangeDetectorRef);

    /**
     * element ID for focused Term
     */
    focusedTerm = '';
    terms: string[];

    readonly loading$ = new BehaviorSubject<boolean>(false);
    readonly error$ = new BehaviorSubject<boolean>(false);

    private readonly _term$ = new BehaviorSubject<string>('');
    private readonly terms$ = this._term$.pipe(
        filter((term) => term !== ''),
        tap(() => {
            this.loading$.next(true);
            this.terms = undefined;
        }),
        debounceTime(500),
        switchMap((term) => {
            this.loading$.next(true);
            this.error$.next(false);
            this.terms = undefined;
            return this.httpService.search(term).pipe(
                tap((results) => {
                    this.terms = results.relatedTerms.map((relatedTerm) => relatedTerm.term);
                    this.loading$.next(false);
                }),
                catchError(() => of(this.error$.next(true))),
            );
        }),
    );

    private readonly subscriptions$ = new Subscription();

    get hasExtendedExplore() {
        return this.userService.access.includes('explore-extended');
    }

    ngOnInit(): void {
        this.subscriptions$.add(this.terms$.subscribe());
    }

    ngOnDestroy(): void {
        this.subscriptions$.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['value'] !== undefined) {
            this._term$.next(changes['value'].currentValue);
        }
    }

    onTermClick(event: MouseEvent, term: string) {
        event.preventDefault();
        this.selected.emit(term);
    }

    onTermEnter(term: string) {
        this.selected.emit(term);
    }

    onTermFocus(term: string) {
        this.focusedTerm = term;
    }

    onArrowDown(): void {
        if (this.loading$.value === true || this.terms.length === 0) return;

        const index = this.items.toArray().findIndex((item) => item.nativeElement.id === this.focusedTerm) + 1;
        if (index > this.items.length - 1) return;

        this.items.toArray()[index].nativeElement.focus();
        this.cdr.detectChanges();
    }

    onArrowUp(): void {
        if (this.loading$.value === true || this.terms.length === 0) return;

        const index = this.items.toArray().findIndex((item) => item.nativeElement.id === this.focusedTerm) - 1;
        if (index < 0) return;

        this.items.toArray()[index].nativeElement.focus();
        this.cdr.detectChanges();
    }
}
